import type {
  CountryPreferenceDto,
  CountryPreferenceInputDto,
  UserDto,
} from "@reactive-resume/dto";
import { useMutation } from "@tanstack/react-query";
import { isAxiosError } from "axios";

import { axios } from "@/client/libs/axios";
import { queryClient } from "@/client/libs/query-client";

const getRequestErrorMessage = (error: unknown): string => {
  if (
    isAxiosError<{
      message?: string | string[];
    }>(error)
  ) {
    const message = error.response?.data?.message;

    if (Array.isArray(message)) {
      return message.join(" ");
    }

    if (typeof message === "string") {
      return message;
    }
  }

  if (error instanceof Error) {
    return error.message;
  }

  return "Impossible d’enregistrer votre pays. Veuillez réessayer.";
};

export const saveCountryPreference = async (
  input: CountryPreferenceInputDto,
): Promise<CountryPreferenceDto> => {
  const response = await axios.post<CountryPreferenceDto>("/user/me/country-preference", input);

  return response.data;
};

export const useSaveCountryPreference = () => {
  const mutation = useMutation({
    mutationFn: saveCountryPreference,
    onSuccess: async (preference) => {
      // Mise à jour immédiate : toutes les pages suivent le nouveau pays sans attendre /user/me.
      queryClient.setQueryData<UserDto | null>(["user"], (user) =>
        user ? { ...user, ...preference } : user,
      );

      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["user"] }),
        queryClient.invalidateQueries({ queryKey: ["payment-options"] }),
      ]);
    },
  });

  return {
    saveCountryPreference: mutation.mutateAsync,
    loading: mutation.isPending,
    error: mutation.error ? getRequestErrorMessage(mutation.error) : null,
    reset: mutation.reset,
  };
};
